import React, { useState, useEffect } from 'react';
import { Users, UserPlus, ArrowRight, MapPin, Clock, Phone, RefreshCw } from 'lucide-react';
import apiService from './apiService';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import useWebSocket from './hooks/useWebSocket';

const QueueManagement = () => {
    const { showAlert } = useAlert();
    const { currentUser } = useAuth();
    const [allQueues, setAllQueues] = useState([]);
    const [todaysQueue, setTodaysQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchQueues = async () => {
        try {
            const [queuesResponse, todayResponse] = await Promise.all([
                apiService.getAllQueues(),
                apiService.getTodaysQueue()
            ]);
            
            if (queuesResponse.allQueues) {
                setAllQueues(queuesResponse.allQueues);
            }
            
            if (todayResponse.todaysQueue) {
                setTodaysQueue(todayResponse.todaysQueue);
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error fetching queues:', error);
            showAlert('Failed to load queue data', 'error');
        }
    };

    useEffect(() => {
        fetchQueues();
    }, []);

    // WebSocket for real-time queue updates
    useWebSocket({
        onMessage: (message) => {
            // Refresh queue when any queue update occurs
            if (message.type === 'queue_updated') {
                console.log('Queue updated via WebSocket:', message);
                fetchQueues();
            }
        },
        onConnect: () => {
            console.log('Queue Management connected to real-time updates');
        }
    });

    const handleCallNext = async (location, roomNumber) => {
        try {
            const response = await apiService.callNextPatient(location, roomNumber);
            
            if (response.callNextPatient.success) {
                showAlert(response.callNextPatient.message, 'success');
                fetchQueues();
            } else {
                showAlert(response.callNextPatient.message, 'info');
            }
        } catch (error) {
            console.error('Error calling next patient:', error);
            showAlert('Failed to call next patient', 'error');
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchQueues();
        setTimeout(() => setRefreshing(false), 500);
    };

    const getLocationDisplay = (loc) => {
        const locations = {
            'reception': 'Reception',
            'triage': 'Triage',
            'consultation': 'Consultation',
            'lab': 'Laboratory',
            'pharmacy': 'Pharmacy',
            'billing': 'Billing'
        };
        return locations[loc] || loc;
    };

    const getLocationColor = (loc) => {
        const colors = {
            'reception': 'bg-blue-100 text-blue-800 border-blue-300',
            'triage': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'consultation': 'bg-green-100 text-green-800 border-green-300',
            'lab': 'bg-purple-100 text-purple-800 border-purple-300',
            'pharmacy': 'bg-pink-100 text-pink-800 border-pink-300',
            'billing': 'bg-orange-100 text-orange-800 border-orange-300'
        };
        return colors[loc] || 'bg-gray-100 text-gray-800 border-gray-300';
    };

    if (loading) {
        return <div className="p-6">Loading queue...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold">Queue Management</h2>
                    <p className="text-gray-600 dark:text-gray-400">Monitor and manage patient flow across all locations</p>
                </div>
                <button
                    onClick={handleRefresh}
                    className={`bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 ${refreshing ? 'opacity-50' : ''}`}
                    disabled={refreshing}
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {/* Queue Status by Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allQueues.map((queue) => (
                    <div key={queue.location} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border-l-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">{getLocationDisplay(queue.location)}</h3>
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getLocationColor(queue.location)}`}>
                                {queue.totalWaiting} Waiting
                            </span>
                        </div>

                        {/* Now Serving */}
                        {queue.nowServing ? (
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                                <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">NOW SERVING</div>
                                <div className="text-2xl font-bold text-green-900 dark:text-green-300">
                                    #{String(queue.nowServing.queueNumber).padStart(3, '0')}
                                </div>
                                <div className="text-sm text-gray-700 dark:text-gray-300">{queue.nowServing.name}</div>
                                {queue.nowServing.roomNumber && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {queue.nowServing.roomNumber}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 text-center text-gray-500 dark:text-gray-400">
                                No one being served
                            </div>
                        )}

                        {/* Call Next Button */}
                        {currentUser?.role !== 'patient' && (
                            <button
                                onClick={() => handleCallNext(queue.location, null)}
                                disabled={queue.totalWaiting === 0}
                                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <UserPlus className="w-4 h-4" />
                                Call Next Patient
                            </button>
                        )}

                        {/* Waiting Queue Preview */}
                        {queue.waitingQueue.length > 0 && (
                            <div className="mt-4 pt-4 border-t dark:border-gray-700">
                                <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">NEXT IN QUEUE:</div>
                                <div className="flex flex-wrap gap-2">
                                    {queue.waitingQueue.slice(0, 5).map((patient) => (
                                        <span 
                                            key={patient.id}
                                            className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-sm font-medium"
                                        >
                                            #{String(patient.queueNumber).padStart(3, '0')}
                                        </span>
                                    ))}
                                    {queue.waitingQueue.length > 5 && (
                                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-sm">
                                            +{queue.waitingQueue.length - 5} more
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Today's Complete Queue */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Clock className="w-6 h-6" />
                    Today's Check-Ins ({todaysQueue.length})
                </h3>

                {todaysQueue.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Queue #</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Patient</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Current Location</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Next Location</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Room</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Check-In Time</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {todaysQueue.map((patient) => (
                                    <tr key={patient.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-4 py-3">
                                            <span className="text-xl font-bold text-blue-600">
                                                #{String(patient.queueNumber).padStart(3, '0')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{patient.name}</div>
                                            <div className="text-sm text-gray-500">{patient.patientId}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLocationColor(patient.currentLocation)}`}>
                                                {getLocationDisplay(patient.currentLocation)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {patient.nextLocation && (
                                                <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                                                    <ArrowRight className="w-4 h-4" />
                                                    {getLocationDisplay(patient.nextLocation)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            {patient.roomNumber || '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                patient.status === 'waiting' ? 'bg-yellow-100 text-yellow-800' :
                                                patient.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                                'bg-green-100 text-green-800'
                                            }`}>
                                                {patient.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {patient.checkInTime ? new Date(patient.checkInTime).toLocaleTimeString() : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <Users className="w-12 h-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                        <p>No patients checked in today</p>
                    </div>
                )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Queue Management Info</h4>
                <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-400">
                    <li>• <strong>Real-time Updates:</strong> WebSocket connection for instant queue updates</li>
                    <li>• <strong>Queue Numbers:</strong> Auto-assigned on check-in, reset daily</li>
                    <li>• <strong>Flow:</strong> Reception → Triage → Consultation → Pharmacy → Billing → Completed</li>
                    <li>• <strong>Display Board:</strong> Use /queue-display route on TV/monitor</li>
                </ul>
            </div>
        </div>
    );
};

export default QueueManagement;

